import { FormEvent, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router'
import { Alert, Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material'
import { api, ApiError } from '../api/client.js'
import { HappyOpsLogo } from '../components/HappyOpsLogo.js'

const PASSWORD_MIN_LENGTH = 8

/**
 * Zielseite der Reset-Links aus den E-Mails (?token=…): setzt über
 * /api/auth/reset-password ein neues Passwort. Wird sowohl beim ersten
 * Setzen nach Account-Anlage als auch nach "Passwort vergessen" verwendet.
 */
export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`)
      return
    }
    if (password !== passwordRepeat) {
      setError('Die Passwörter stimmen nicht überein.')
      return
    }
    setSubmitting(true)
    try {
      await api.resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? 'Der Link ist ungültig oder abgelaufen. Fordere über „Passwort vergessen“ einen neuen an.'
          : 'Zurücksetzen fehlgeschlagen. Bitte später erneut versuchen.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
    >
      <Paper sx={{ p: 4, width: 360 }} elevation={3}>
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <HappyOpsLogo width={36} height={36} />
          <Typography variant="h5">Neues Passwort</Typography>
        </Box>
        {token.length === 0 ? (
          <Stack spacing={2}>
            <Alert severity="error">
              Der Link ist unvollständig — bitte verwende den Link aus der E-Mail oder fordere
              einen neuen an.
            </Alert>
            <Link component={RouterLink} to="/forgot-password">
              Neuen Link anfordern
            </Link>
          </Stack>
        ) : done ? (
          <Stack spacing={2}>
            <Alert severity="success">
              Dein Passwort wurde gesetzt. Du kannst dich jetzt anmelden.
            </Alert>
            <Link component={RouterLink} to="/login">
              Zur Anmeldung
            </Link>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Neues Passwort"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                helperText={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`}
                autoFocus
                required
              />
              <TextField
                label="Passwort wiederholen"
                type="password"
                value={passwordRepeat}
                onChange={(e) => setPasswordRepeat(e.target.value)}
                autoComplete="new-password"
                required
              />
              <Button type="submit" variant="contained" disabled={submitting}>
                Passwort setzen
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Box>
  )
}
